<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <b-alert :show="isLocked" variant="danger" class="font-weight-bold text-center">
            No Edits Allowed - Existing Replies or No Permissions
        </b-alert>

        <div v-if="initialLoading" key="initialLoading">
            <busy-spinner
                inner-class="py-5"
            ></busy-spinner>
        </div>

        <div v-else key="notLoading">

            <b-overlay :show="showSuccess">
                <template #overlay>
                    <div class="text-center">
                        <b-icon icon="check-circle" variant="success" font-scale="3"></b-icon>
                        <h4 class="mt-3">Saved</h4>
                    </div>
                </template>

                <FormulateForm
                    v-model="docRequestData"
                    name="docRequestForm"
                    :form-errors="formErrorsArray"
                    :errors="inputErrorsObject"
                    @submit="handleSubmit"
                >
                    <FormulateErrors />
                    <FormulateInput
                        name="title"
                        type="text"
                        label="Title"
                        validation="required"
                        help="Name/title of the document to be uploaded by vendors"
                        :disabled="isLocked"
                        :outer-class="['formulate-horizontal']"
                    />
                    <FormulateInput
                        name="instructions"
                        type="textarea"
                        label="Instructions for Vendors"
                        placeholder="Instructions for vendors..."
                        rows="5"
                        help="Provide detailed instructions for how vendors should fill in the template, or what file to upload."
                        :disabled="isLocked"
                        :outer-class="['formulate-horizontal']"
                    />
                    <FormulateInput
                        name="allow_multiple_reply_files"
                        type="radio"
                        label="Allow single or multiple file uploads?"
                        help="If you select single, vendors can only upload one file. If you select multiple, vendors can upload multiple files for this response item."
                        help-position="after"
                        :options="allowMultipleReplyFilesOptions"
                        :disabled="isLocked"
                        :outer-class="['formulate-horizontal']"
                    />
                    <FormulateInput
                        id="docrequest-formulate-file-input"
                        v-if="docRequestData.dr_type === 'template'"
                        ref="formulateFileInput"
                        type="file"
                        name="template_file"
                        label="Template File"
                        help="Upload the template or reference file to be completed by vendors"
                        :disabled="isLocked"
                        :upload-area-mask-class="['bg-gray-100']"
                        :outer-class="['formulate-horizontal']"
                        :uploader="formulateUploader"
                        upload-behavior="live"
                        @input="setUploadedFiles"
                    />
                </FormulateForm>  

                <div class="my-3 d-flex justify-content-center">
                    <b-button
                        variant="gray-secondary"
                        size="lg"
                        class="mr-3"
                        @click="triggerCancel"
                    >
                        <span>Cancel</span>
                    </b-button>
                    <b-button
                        variant="primary"
                        size="lg"
                        :disabled="saving || uploadPending || isLocked"
                        @click="triggerSubmit"
                    >
                        <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                        <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                        <span>{{ saving ? 'Saving' : 'Save' }}</span>
                    </b-button>
                </div>
            </b-overlay>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import pick from 'lodash/pick';
//import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'DocRequestSetup',
    components: {},
    props: {
        docRequestId: {  // pass in the entire docRequest object -- no.. that's too much to expect... not the same as other linked_models
            // but maybe you need this to make formulate file input work???
            // we'll use it to get the ID, and also use it in .setLocalData() in created hook
            type: String,
            required: true,
        },
        readOnly: {  // NOTE - there is something in data called 'isLocked' (based on API data) but setting this prop true will also set that
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            // formulate form errors
            formErrorsArray: [],  // set this with list of errors from API or other source if there are any
            inputErrorsObject: {},  // set this with object of errors from API or other source if there are any, keys are field names as known to formulate
            initialLoading: true,  // set to TRUE to make sure form is hidden until we have initial data -- 
            // can't figure out how to 'refresh' the file field after setting data if form was shown before we have data... 
            // but if we set data first before showing form, it works
            uploadPending: false,
            saving: false,
            showSuccess: false,
            fileCategory: 'docrequest_templates',
            docRequestData: {},  // v-model with formulate form -- local version of data
            allowMultipleReplyFilesOptions: [  // vue-formulate doesn't work well with booleans as values, so we have to convert to these strings in both directions
                { value: 'single', label: 'Single' },    
                { value: 'multiple', label: 'Multiple' },
            ],
            //fileInput: null,  // v-model with formulate file input
            uploadedFiles: null,
            isLocked: false,  // set to TRUE if you want to block edits (ie: if has_submitted_replies = true)
        }
    },
    computed: {

    },
    created: function() {
        //this.setLocalData(this.docRequestObj);
    },
    mounted: function() {
        this.getData();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        getData: function() {
            // get initial data
            this.initialLoading = true;
            return suppliosApi.docRequest.retrieve(this.docRequestId)
                .then(response => {
                    return this.setLocalData(response.data);  // this returns true when done so you can chain promises correctly
                })
                .catch(error => {
                    this.handleError(error, 'Error while loading data');
                    return false;
                })
                .then(success => {
                    this.initialLoading = false;
                    return success;
                });
        },
        formulateUploader: function(file, progress, error, options) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = this.fileCategory;
            let parentId = null;  // the ID of the 'parent' for this file - optional
            let title = '';  // you could pass title to asyncSplFileSlinger if you know it in advance
            let uploadResponse = asyncSplFileSlinger(file_category, file, progress, error, options, title, parentId);
            let finalReturn = uploadResponse.then(resp => {
                this.uploadPending = false;
                console.log('Dumping resp from our file uploader:');
                console.log(resp);
                return resp;
            });
            return finalReturn;
        },
        setUploadedFiles: function(payload) {
            this.uploadedFiles = payload;
        },
        setLocalData: function(apiData) { 
            // IMPORTANT - return true when done so we chain promises accordingly, make sure we don't show form until this is DONE
            // set the local formulate v-model object with data from API or prop data object, particularly to make the file input work
            let localData = pick(apiData, ['id', 'instructions', 'title', 'dr_type']);  // dr_type is either 'upload' or 'template'
            // have to handle 'allow_multiple_reply_files' as a string, not boolean because vue-formulate doesn't like booleans
            localData['allow_multiple_reply_files'] = apiData.allow_multiple_reply_files ? 'multiple' : 'single';
            // then take 'template_file' object, get the 'plain_url' and make an array of objects with 'url' key, that's what vue-formulate file input expects
            if (apiData.template_file && apiData.template_file.plain_url) {
                // it should be OK to set this whether dr_type is 'template' or 'upload'
                localData['template_file'] = [{url: apiData.template_file.plain_url}];
            }
            console.log('Setting local data as this:');
            console.log(localData);
            this.docRequestData = Object.assign({}, localData);
            // now set this.isLocked -- if you have this.has_submitted_replies and its true
            this.isLocked = this.readOnly || (apiData.has_submitted_replies === true) ? true : false;
            console.log(this.isLocked ? 'DocRequest IS LOCKED' : 'DocRequest is not locked');
            // NONE OF THESE METHODS BELOW WORK to update the form with new data if form was shown first... so don't render form until you fetch existing values
            //this.docRequestData = cloneDeep(localData);
            //this.$formulate.setValues('docRequestForm');
            //this.$formulate.setValues('docRequestForm', this.docRequestData);  // try setting the form model again
            //this.waitingForInitialData = false;  // finally show the form now that you have data
            return true;
        },
        triggerSubmit: function() {
            this.$formulate.submit('docRequestForm');
        },
        triggerCancel: function() {
            this.$emit('cancel-close');
        },
        handleSubmit: function(formData) {
            console.log('From handleSubmit, here is formData we got:');
            console.log(formData);
            let pickedData = pick(formData, ['id', 'instructions', 'title', 'dr_type']);  // dr_type is 'template' or 'upload'
            // convert allow_multiple_reply_files back to boolean from yes/no strings
            pickedData['allow_multiple_reply_files'] = formData.allow_multiple_reply_files === 'multiple' ? true : false;
            if (formData.template_file && formData.template_file.length && formData.template_file[0].id) {
                // 'template_file' should be the file ID -- only add it to the post data if you have the id... which you won't have if it was an existing file (due to how vue-formulate works)
                // if you don't have template_file id, it won't even be in the post data!
                // this is all because of a bug in vue-formulate 2.4.5... had to regress to 2.4.4 to make it work
                pickedData['template_file'] = formData.template_file[0].id;  // vue formulate always makes an array of files, you have to pull out first one, and grab the id
            }
            this.saving = true;
            return suppliosApi.docRequest.update(this.docRequestId, pickedData)
                .then(response => {
                    //this.setLocalData(response.data);
                    console.log(response.data);
                    this.showSuccess = true;
                    setTimeout(() => {
                        this.$emit('save-close');
                    }, 2000);
                    return true;
                })
                .catch(error => {
                    // try to get errors from API and set them in the form
                    if (error.response && error.response.data) {
                        const { non_field_errors, ...fieldErrors } = error.response.data;
                        this.$formulate.handle({
                            formErrors: non_field_errors || [],
                            inputErrors: fieldErrors || {},
                        }, 'docRequestForm');
                    } else {
                        this.handleError(error, 'Error while updating data');
                    }
                    return false;
                })
                .then(success => {
                    this.saving = false;
                    return success;
                });
        }
    }
}
</script>

<style>

</style>